import { Col, notification, Row } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useOutletContext } from "react-router-dom";
import { VerifiedUploadField } from "../../../Components/FormComponents/InputFields";
import { uploadDocumentsAPI } from "../../../Services/EmployeeOnboarding";
import axios from "axios";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

const ALL_DOCUMENT_FIELDS = [
  "photo",
  "resume",
  "emp_sign",
  "pan",
  "aadhaar_card_front",
  "aadhaar_card_back",
  "cheque",
  "id_proof_passport",
  "id_proof_voter_id_front",
  "id_proof_voter_id_back",
  "id_proof_dl_front",
  "id_proof_dl_back",
  "cert_10",
  "cert_12",
  "grad",
  "pg",
  "any_other",
];

// OCR extraction only runs on these fields; the API's document_type for the
// uploaded file must match the mapped value or it's flagged as a mismatch.
const OCR_EXPECTED_DOCUMENT_TYPES = {
  cert_10: "certificate_10th",
  cert_12: "certificate_12th",
  grad: "certificate_graduation",
  pg: "certificate_graduation",
};

export default function UploadDocuments({ current, setCurrent }) {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {},
  });

  const type = localStorage.getItem("userType");

  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const { responseData, id, profileData } = useOutletContext();
  const [disabled, setDisabled] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldStatuses, setFieldStatuses] = useState({});

  const handleStatusChange = useCallback((fieldId, status) => {
    setFieldStatuses((prev) =>
      prev[fieldId] === status ? prev : { ...prev, [fieldId]: status },
    );
  }, []);

  const onSubmit = async (val) => {
    if (!isAllFieldsValid()) {
      api.error({
        message: "Validation Error",
        description:
          failedCount > 0
            ? "Some documents could not be verified. Please re-upload a clearer copy before continuing."
            : "Please upload all mandatory documents (Photo, Resume, PAN, Aadhaar, Sign, etc.).",
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();

    const appendSmartFile = (key, value) => {
      if (!value) return;

      if (Array.isArray(value)) {
        const file = value[0];
        if (!file) return;

        if (file instanceof File) {
          formData.append(key, file);
        } else if (file?.originFileObj instanceof File) {
          formData.append(key, file.originFileObj);
        }
      } else if (value instanceof File) {
        formData.append(key, value);
      } else if (value?.originFileObj instanceof File) {
        formData.append(key, value.originFileObj);
      }
    };

    // Append all fields
    appendSmartFile("passport_photo", val?.photo);
    appendSmartFile("updated_resume", val?.resume);
    appendSmartFile("pan_card", val?.pan);
    appendSmartFile("aadhaar_card_front", val?.aadhaar_card_front);
    appendSmartFile("aadhaar_card_back", val?.aadhaar_card_back);
    appendSmartFile("relieving_letter", val?.relieving);
    appendSmartFile("salary_slips", val?.salary_slips);
    appendSmartFile("emp_sign", val?.emp_sign);

    appendSmartFile("cancelled_cheque", val?.cheque);
    appendSmartFile("id_proof_passport", val?.id_proof_passport);
    appendSmartFile("id_proof_voter_id_front", val?.id_proof_voter_id_front);
    appendSmartFile("id_proof_voter_id_back", val?.id_proof_voter_id_back);
    appendSmartFile("id_proof_dl_front", val?.id_proof_dl_front);
    appendSmartFile("id_proof_dl_back", val?.id_proof_dl_back);
    appendSmartFile("tenth_certificate", val?.cert_10);
    appendSmartFile("twelfth_certificate", val?.cert_12);
    appendSmartFile("graduation_certificate", val?.grad);
    appendSmartFile("post_graduation_certificate", val?.pg);
    appendSmartFile("any_other", val?.any_other);

    // Check if FormData has any file entries before uploading.
    if ([...formData.entries()].length === 0) {
      setIsLoading(false);
      navigate("/new-user/nominees");
      console.log("No files to upload, skipping API call.");
      api.error({
        message: "No files selected for upload.",
        placement: "top",
      });
      return;
    }

    // Mark the documents step as completed for this submission.
    formData.append("is_completed", true);

    // Debug what's being sent
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    try {
      const { data } = await uploadDocumentsAPI(formData);
      setIsLoading(false);
      if (data?.success) {
        console.log(responseData?.final_submit);
        if (responseData?.final_submit) {
          await handleSubmitFinal();
        } else {
          navigate("/new-user/nominees");
        }
        api.success({
          message: data?.message,
          placement: "top",
        });
        navigate("/new-user/nominees");
      } else {
        api.error({
          message: data?.message || "Something went wrong",
          placement: "top",
        });
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      api.error({
        message: "Upload failed. Please try again.",
        placement: "top",
      });
      setIsLoading(false);
    }
  };

  const handleSubmitFinal = async () => {
    const { data } = await axios.post("/api/employees/final-submit", {
      final_submit: true,
      sections: ["documents"],
    });

    if (data?.success) {
      api.success({
        message: data?.message,
        placement: "top",
      });
      navigate("/new-user/nominees");
    } else {
      api.error({
        message: data?.message,
        placement: "top",
      });
    }
  };

  const { documents_meta } = responseData?.final_submit || {};

  const is_Final_submit = documents_meta?.final_submit === true;

  const shouldDisableFields = (sectionMeta) => {
    if (!sectionMeta) return false;
    return sectionMeta.final_submit === true;
  };
  const disabledDocuments = shouldDisableFields(documents_meta);

  const isFieldDisabled = (fieldName) => {
    if (disabled[fieldName]) return true;
    if (!disabledDocuments) return false;

    const isFlagged = responseData?.flagged_fields?.some(
      (f) => f.id === fieldName,
    );
    return !isFlagged;
  };

  const lobName = (
    responseData?.lob ||
    responseData?.line_of_business ||
    responseData?.sub_vertical ||
    responseData?.business_unit ||
    ""
  ).toLowerCase();

  // Driving License is mandatory only for the JLG LOB.
  const isJlgLob = lobName.trim() === "jlg";
  console.log(responseData);

  useEffect(() => {
    const personal = responseData?.documents?.personal_documents;
    const edu = responseData?.documents?.educational_certificates;
    const personalProfile = profileData?.documents?.personal_documents;
    const eduProfile = profileData?.documents?.educational_certificates;

    if (!personal || !edu) return;

    const handleField = (name, profileVal, fallbackVal) => {
      if (profileVal) {
        if (Array.isArray(profileVal)) {
          const urls = profileVal
            .filter((item) => item?.url)
            .map((item) => item.url);
          setValue(name, urls);
        } else {
          setValue(name, [profileVal]);
        }
        setDisabled((prev) => ({ ...prev, [name]: true }));
      } else if (fallbackVal) {
        if (Array.isArray(fallbackVal)) {
          const urls = fallbackVal
            .filter((item) => item?.url)
            .map((item) => item.url);
          setValue(name, urls);
        } else {
          setValue(name, [fallbackVal]);
        }
        setDisabled((prev) => ({ ...prev, [name]: false }));
      } else {
        setValue(name, []);
        setDisabled((prev) => ({ ...prev, [name]: false }));
      }
    };

    // Personal Documents
    handleField(
      "photo",
      personalProfile?.passport_photos,
      personal?.passport_photos,
    );
    handleField(
      "resume",
      personalProfile?.updated_resume?.url,
      personal?.updated_resume?.url,
    );
    handleField("pan", personalProfile?.pan_card?.url, personal?.pan_card?.url);
    handleField(
      "aadhaar",
      personalProfile?.aadhaar_card?.url,
      personal?.aadhaar_card?.url,
    );
    handleField(
      "aadhaar_card_front",
      personalProfile?.aadhaar_card_front?.url ||
        personalProfile?.aadhaar_card?.url,
      personal?.aadhaar_card_front?.url || personal?.aadhaar_card?.url,
    );
    handleField(
      "aadhaar_card_back",
      personalProfile?.aadhaar_card_back?.url,
      personal?.aadhaar_card_back?.url,
    );
    handleField(
      "relieving",
      personalProfile?.relieving_letter?.url,
      personal?.relieving_letter?.url,
    );
    handleField(
      "emp_sign",
      personalProfile?.emp_sign?.url,
      personal?.emp_sign?.url,
    );
    handleField(
      "salary_slips",
      personalProfile?.salary_slips,
      personal?.salary_slips,
    );
    handleField(
      "cheque",
      personalProfile?.cancelled_cheque_passbook?.url,
      personal?.cancelled_cheque_passbook?.url,
    );
    handleField(
      "id_proof",
      personalProfile?.id_proof?.url,
      personal?.id_proof?.url,
    );
    handleField(
      "id_proof_passport",
      personalProfile?.id_proof_passport?.url || personalProfile?.id_proof?.url,
      personal?.id_proof_passport?.url || personal?.id_proof?.url,
    );
    handleField(
      "id_proof_voter_id_front",
      personalProfile?.id_proof_voter_id_front?.url,
      personal?.id_proof_voter_id_front?.url,
    );
    handleField(
      "id_proof_voter_id_back",
      personalProfile?.id_proof_voter_id_back?.url,
      personal?.id_proof_voter_id_back?.url,
    );
    handleField(
      "id_proof_dl_front",
      personalProfile?.id_proof_dl_front?.url,
      personal?.id_proof_dl_front?.url,
    );
    handleField(
      "id_proof_dl_back",
      personalProfile?.id_proof_dl_back?.url,
      personal?.id_proof_dl_back?.url,
    );

    // Educational Documents
    handleField(
      "cert_10",
      eduProfile?.tenth_certificate?.url,
      edu?.tenth_certificate?.url,
    );
    handleField(
      "cert_12",
      eduProfile?.twelfth_certificate?.url,
      edu?.twelfth_certificate?.url,
    );
    handleField(
      "grad",
      eduProfile?.graduation_certificate?.url,
      edu?.graduation_certificate?.url,
    );
    handleField(
      "pg",
      eduProfile?.post_graduation_certificate?.url,
      edu?.post_graduation_certificate?.url,
    );
    handleField(
      "any_other",
      eduProfile?.other_certificate?.url,
      edu?.other_certificate?.url,
    );
  }, [responseData, profileData, setValue]);
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const allFormValues = watch();

  const totalDocumentFields = ALL_DOCUMENT_FIELDS.length;
  const uploadedCount = ALL_DOCUMENT_FIELDS.filter((field) => {
    const val = allFormValues[field];
    return val && (Array.isArray(val) ? val.length > 0 : true);
  }).length;
  const extractedCount = ALL_DOCUMENT_FIELDS.filter(
    (field) => fieldStatuses[field] === "extracted",
  ).length;
  const failedCount = ALL_DOCUMENT_FIELDS.filter(
    (field) =>
      fieldStatuses[field] === "failed" || fieldStatuses[field] === "mismatch",
  ).length;
  const pendingCount = totalDocumentFields - uploadedCount;

  const isAllFieldsValid = () => {
    // Basic mandatory documents
    const basicMandatory = [
      "photo",
      "resume",
      "emp_sign",
      "pan",
      "aadhaar_card_front",
      "aadhaar_card_back",
      "cheque",
      // "id_proof_voter_id_front",
      // "id_proof_voter_id_back",
      "cert_10",
      "cert_12",
    ];

    const isBasicValid = basicMandatory.every((field) => {
      const val = allFormValues[field];
      return val && (Array.isArray(val) ? val.length > 0 : true);
    });

    if (!isBasicValid) return false;

    // CV, Gold, and LAP do not require a Driving License.
    if (isJlgLob) {
      if (!allFormValues.id_proof_dl_front || !allFormValues.id_proof_dl_back) {
        return false;
      }
    }

    // Any document whose OCR extraction failed must be re-uploaded before submit.
    if (failedCount > 0) return false;

    return true;
  };

  return (
    <>
      {contextHolder}
      <h3 className="color-heading mb-4">Documents</h3>
      <form className="h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className=" border rounded-xl bg-white shadow-md">
          {/* HEADER */}
          <div
            className="flex justify-between items-center px-4 py-3 border rounded-t-xl cursor-pointer"
            onClick={() => toggleAccordion(0)}
          >
            <h3 className="color-heading2">DOCUMENTS</h3>

            <MdKeyboardArrowDown
              size={35}
              className={`transition-transform duration-300 ${
                activeIndex === 0 ? "rotate-180" : ""
              }`}
            />
          </div>
          {/* BODY */}
          {activeIndex === 0 && (
            <div className={`transition-all duration-300 overflow-hidden p-4`}>
              <Row className="align-content-start w-full">
                {/* Document Upload Fields */}
                <VerifiedUploadField
                  id="photo"
                  name="Passport Size Photograph"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("photo")}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="resume"
                  name="Updated Resume"
                  disabled={isFieldDisabled("resume")}
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="emp_sign"
                  name="Signature"
                  disabled={isFieldDisabled("emp_sign")}
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="pan"
                  name="PAN Card"
                  control={control}
                  onStatusChange={handleStatusChange}
                  disabled={isFieldDisabled("pan")}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />

                <VerifiedUploadField
                  id="aadhaar_card_front"
                  name="Aadhar Card (Front)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  disabled={isFieldDisabled("aadhaar_card_front")}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="aadhaar_card_back"
                  name="Aadhar Card (Back)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  disabled={isFieldDisabled("aadhaar_card_back")}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="cheque"
                  name="Cancelled Cheque / Passbook"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("cheque")}
                  validations={{
                    required: true,
                  }}
                />

                <VerifiedUploadField
                  id="id_proof_passport"
                  name="Passport"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("id_proof_passport")}
                  validations={{
                    required: false,
                  }}
                />

                <VerifiedUploadField
                  id="id_proof_voter_id_front"
                  name="Voter ID (Front)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("id_proof_voter_id_front")}
                  // validations={{
                  //   required:
                  //     responseData?.company === "DJT Financial Services Pvt Ltd" ||
                  //     responseData?.company === "Djt Microfinance Private Limited",
                  // }}
                  // validations={{
                  //   required: true,
                  // }}
                />

                <VerifiedUploadField
                  id="id_proof_voter_id_back"
                  name="Voter ID (Back)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("id_proof_voter_id_back")}
                  // validations={{
                  //   required:
                  //     responseData?.company === "DJT Financial Services Pvt Ltd" ||
                  //     responseData?.company === "Djt Microfinance Private Limited",
                  // }}
                  // validations={{
                  //   required: true,
                  // }}
                />

                <VerifiedUploadField
                  id="id_proof_dl_front"
                  name="Driving License (Front)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("id_proof_dl_front")}
                  // validations={{
                  //   required:
                  //     responseData?.company === "DJT Financial Services Pvt Ltd" ||
                  //     responseData?.company === "Djt Microfinance Private Limited",
                  // }}
                  validations={{
                    required: isJlgLob,
                  }}
                />

                <VerifiedUploadField
                  id="id_proof_dl_back"
                  name="Driving License (Back)"
                  control={control}
                  onStatusChange={handleStatusChange}
                  errors={errors}
                  disabled={isFieldDisabled("id_proof_dl_back")}
                  // validations={{
                  //   required:
                  //     responseData?.company === "DJT Financial Services Pvt Ltd" ||
                  //     responseData?.company === "Djt Microfinance Private Limited",
                  // }}
                  validations={{
                    required: isJlgLob,
                  }}
                />

                <VerifiedUploadField
                  id="cert_10"
                  disabled={isFieldDisabled("cert_10")}
                  name="Upload 10th Certificate"
                  control={control}
                  onStatusChange={handleStatusChange}
                  expectedDocumentType={OCR_EXPECTED_DOCUMENT_TYPES.cert_10}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="cert_12"
                  name="Upload 12th Certificate"
                  control={control}
                  onStatusChange={handleStatusChange}
                  expectedDocumentType={OCR_EXPECTED_DOCUMENT_TYPES.cert_12}
                  disabled={isFieldDisabled("cert_12")}
                  errors={errors}
                  validations={{
                    required: true,
                  }}
                />
                <VerifiedUploadField
                  id="grad"
                  name="Upload Graduation Certificate"
                  control={control}
                  onStatusChange={handleStatusChange}
                  expectedDocumentType={OCR_EXPECTED_DOCUMENT_TYPES.grad}
                  disabled={isFieldDisabled("grad")}
                  errors={errors}
                  validations={{
                    required: false,
                  }}
                />
                <VerifiedUploadField
                  id="pg"
                  name="Upload Post Graduation Certificate"
                  control={control}
                  onStatusChange={handleStatusChange}
                  expectedDocumentType={OCR_EXPECTED_DOCUMENT_TYPES.pg}
                  disabled={isFieldDisabled("pg")}
                  errors={errors}
                />
                <VerifiedUploadField
                  id="any_other"
                  name="Any Other Certificate"
                  control={control}
                  onStatusChange={handleStatusChange}
                  disabled={isFieldDisabled("any_other")}
                  errors={errors}
                />
              </Row>
            </div>
          )}
        </div>
        <Row
          className="step-buttons-box justify-end pt-3 px-2"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <button
            type="button"
            className="my-2 step-button mr-2 back-button"
            style={{
              backgroundColor: "#FFFFFF",
              color: "#A4A4A4",
              border: "1px solid #A4A4A4",
            }}
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </button>
          <button
            disabled={
              (is_Final_submit && !responseData?.flagged_fields?.length) ||
              isLoading
            }
            className="my-2 text-white step-button"
            type="submit"
          >
            {isLoading ? "Processing..." : "Save & Continue"}
          </button>
        </Row>
      </form>
    </>
  );
}
